import React, { useState, useEffect, useCallback } from "react";
import DeviceList from "./DeviceList";
import DeviceDetail from "./DeviceDetail";
import OperationsPanel from "./OperationsPanel";
import { createClient, getItems, getLoginInfo } from "../api";
import { pickDeviceString } from "../lib/deviceFlags";
import {
  fetchWorkerStatus,
  getMediaModeLabel,
  isLocalMediaMode,
  requestWorkerReload,
} from "../lib/streamUrls";

const EMPTY_FORM = {
  deviceName: "",
  ipAddress: "",
  rtmPpushLink: "",
  streamLinkWebRTC: "",
  streamLinkHLS: "",
  isActive: true,
  isAiProcess: true,
};

export default function Dashboard({ token, setToken }) {
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [streamModes, setStreamModes] = useState({});
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState({});
  const [editTarget, setEditTarget] = useState(undefined);
  const [form, setForm] = useState(EMPTY_FORM);
  const [theme] = useState("light");
  const [workerStatus, setWorkerStatus] = useState(null);
  const [statusError, setStatusError] = useState("");
  const [reloading, setReloading] = useState(false);
  const apiClient = createClient(token);

  useEffect(() => {
    getLoginInfo(token)
      .then((info) => setUser(info.user || {}))
      .catch((err) => console.error("Failed to load profile", err));
  }, [token]);

  const pollWorkerStatus = useCallback(async () => {
    try {
      const data = await fetchWorkerStatus();
      setWorkerStatus(data);
      setStatusError("");
    } catch (err) {
      setStatusError(err.message || "unavailable");
    }
  }, []);

  useEffect(() => {
    pollWorkerStatus();
    const id = setInterval(pollWorkerStatus, 7000);
    return () => clearInterval(id);
  }, [pollWorkerStatus]);

  const fetchDevices = async () => {
    setLoading(true);
    try {
      const items = await getItems(apiClient, "/DeviceSensor/GetAll");
      setDevices(items);
      setStreamModes((prev) => {
        const next = { ...prev };
        items.forEach((item) => {
          if (!next[item.id]) next[item.id] = "webrtc";
        });
        return next;
      });
    } catch (err) {
      console.error("Failed to fetch devices", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices();
  }, [token]);

  const handleLogout = () => {
    localStorage.removeItem("auth_token");
    setToken(null);
  };

  const openCreateModal = () => {
    setEditTarget(null);
    setForm(EMPTY_FORM);
  };

  const openEditModal = (device) => {
    setEditTarget(device);
    setForm({
      deviceName: device.deviceName || "",
      ipAddress: pickDeviceString(device, "ipAddress", "IpAddress"),
      rtmPpushLink: pickDeviceString(device, "rtmPpushLink", "RtmPpushLink"),
      streamLinkWebRTC: pickDeviceString(
        device,
        "streamLinkWebRTC",
        "StreamLinkWebRTC"
      ),
      streamLinkHLS: pickDeviceString(device, "streamLinkHLS", "StreamLinkHLS"),
      isActive: device.isActive !== false && device.IsActive !== false,
      isAiProcess: device.isAiProcess !== false && device.IsAiProcess !== false,
    });
  };

  const closeModal = () => setEditTarget(undefined);

  const saveDevice = async () => {
    if (!apiClient) return;

    const payload = {
      ...(editTarget || {}),
      deviceName: form.deviceName,
      ipAddress: form.ipAddress,
      rtmPpushLink: form.rtmPpushLink,
      streamLinkWebRTC: form.streamLinkWebRTC,
      streamLinkHLS: form.streamLinkHLS,
      isActive: !!form.isActive,
      isAiProcess: !!form.isAiProcess,
      providesLink: !!(form.streamLinkWebRTC || form.streamLinkHLS),
    };

    try {
      let deviceId;

      if (editTarget) {
        await apiClient.put("/DeviceSensor/Update", payload);
        deviceId = editTarget.id;
      } else {
        const res = await apiClient.post("/DeviceSensor/Create", payload);
        deviceId = res?.data?.result?.id;

        const sceneRes = await apiClient.post("/DeviceScene/Create", {
          deviceSensorId: deviceId,
          sceneConfigHeight: 1080,
          sceneConfigWidth: 1920,
          isRoifilteringstream: true,
          isLineCrossingStream: true,
        });

        const deviceSceneId = sceneRes?.data?.result?.id;

        await apiClient.post("/DeviceRoiFilteringStreamGate/Create", {
          deviceSceneId,
          isEnable: "1",
          isInverseRoi: "0",
          classId: "-1",
          roiGatePointxywh: "0;0;100;100",
        });

        await apiClient.post("/DeviceLineCrossingStream/Create", {
          deviceSceneId,
          isEnable: true,
          direction: "both",
          classID: "-1",
          triggerInterval: "1000",
          lineCrossingSP: "0,0",
          lineCrossingEP: "100,100",
        });
      }

      closeModal();
      await fetchDevices();
      await requestWorkerReload().catch(() => {});
      await pollWorkerStatus();
    } catch (err) {
      console.error("Save device failed", err);
      alert("Device save failed.");
    }
  };

  const deleteDevice = async (device) => {
    if (!apiClient) return;
    if (!window.confirm(`Delete device ${device.deviceName || device.id}?`)) return;

    try {
      const scenes = await getItems(
        apiClient,
        `/DeviceScene/GetAll?deviceSensorId=${device.id}`
      );
      const scene = scenes?.[0];

      if (scene) {
        const sceneId = scene.id;
        await apiClient.delete("/DeviceRoiFilteringStreamGate/Delete", {
          params: { deviceSceneId: sceneId },
        });
        await apiClient.delete("/DeviceLineCrossingStream/Delete", {
          params: { deviceSceneId: sceneId },
        });
        await apiClient.delete("/DeviceScene/Delete", { params: { id: sceneId } });
      }

      await apiClient.delete("/DeviceSensor/Delete", { params: { id: device.id } });

      if (selectedDevice?.id === device.id) setSelectedDevice(null);
      await fetchDevices();
      await requestWorkerReload().catch(() => {});
    } catch (err) {
      console.error("Delete failed", err);
      alert("Delete failed.");
    }
  };

  const updateDeviceFlags = async (device, patch) => {
    if (!apiClient) return;
    const payload = { ...device, ...patch };
    try {
      await apiClient.put("/DeviceSensor/Update", payload);
      await fetchDevices();
      await requestWorkerReload().catch(() => {});
      await pollWorkerStatus();
    } catch (err) {
      console.error("Update device flags failed", err);
      alert("Could not update device. Check API permissions.");
    }
  };

  const handleReloadWorker = async () => {
    setReloading(true);
    try {
      await requestWorkerReload();
      await pollWorkerStatus();
    } catch (err) {
      alert(`Worker sync failed: ${err.message}`);
    } finally {
      setReloading(false);
    }
  };

  const setDeviceStreamMode = (deviceId, mode) => {
    setStreamModes((prev) => ({ ...prev, [deviceId]: mode }));
  };

  const handleDeviceSensorUpdated = (updated) => {
    if (!updated || updated.id == null) return;
    setDevices((prev) =>
      prev.map((d) => (d.id === updated.id ? { ...d, ...updated } : d))
    );
    setSelectedDevice((prev) =>
      prev && prev.id === updated.id ? { ...prev, ...updated } : prev
    );
  };

  return (
    <div className={`dashboard ${theme}`}>
      <header className="dashboard-header">
        <div className="header-brand">
          <h2>AI Camera — EMP Console</h2>
          {user?.name && <span className="header-welcome">Welcome, {user.name}</span>}
        </div>
        <div className="header-actions">
          <button type="button" className="logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      <div className="main-content">
        <div className="left-panel">
          {!selectedDevice ? (
            <>
              <OperationsPanel
                devices={devices}
                workerStatus={workerStatus}
                statusError={statusError}
                onReloadWorker={handleReloadWorker}
                reloading={reloading}
              />
              {loading && <p className="loading-hint">Loading devices…</p>}
              <DeviceList
                devices={devices}
                streamModes={streamModes}
                workerStatus={workerStatus}
                onChangeStreamMode={setDeviceStreamMode}
                onOpen={(device) =>
                  setSelectedDevice({
                    ...device,
                    streamMode: streamModes[device.id] || "webrtc",
                  })
                }
                onEdit={openEditModal}
                onDelete={deleteDevice}
                onAdd={openCreateModal}
                onToggleActive={(dev, checked) =>
                  updateDeviceFlags(dev, { isActive: checked })
                }
                onToggleAi={(dev, checked) =>
                  updateDeviceFlags(dev, { isAiProcess: checked })
                }
              />
            </>
          ) : (
            <div className="device-detail-wrapper">
              <div className="title-row">
                <h3>Live Device View</h3>
                <button type="button" onClick={() => setSelectedDevice(null)}>
                  Back
                </button>
              </div>
              <DeviceDetail
                device={selectedDevice}
                token={token}
                streamMode={selectedDevice.streamMode || "webrtc"}
                workerRuntime={workerStatus?.devices?.find(
                  (d) => Number(d.deviceId) === Number(selectedDevice.id)
                )}
                onDeviceSensorUpdated={handleDeviceSensorUpdated}
              />
            </div>
          )}
        </div>
      </div>

      {editTarget !== undefined && (
        <div className="modal-backdrop">
          <div className="modal-card modal-card--wide">
            <h3>{editTarget ? "Edit Device" : "Add Device"}</h3>
            <label>
              Device name
              <input
                value={form.deviceName}
                onChange={(e) => setForm({ ...form, deviceName: e.target.value })}
              />
            </label>
            <label>
              Camera source (RTSP pull)
              <input
                value={form.ipAddress}
                onChange={(e) => setForm({ ...form, ipAddress: e.target.value })}
                placeholder="rtsp://host:port/h264"
              />
            </label>
            <fieldset className="modal-fieldset">
              <legend>Platform stream links (saved to API)</legend>
              {isLocalMediaMode() && (
                <p className="modal-env-hint">
                  Playback uses <strong>{getMediaModeLabel()}</strong> from{" "}
                  <code>zone_editor/.env</code> until{" "}
                  <code>REACT_APP_MEDIAMTX_PUSH_MODE=production</code>. Fields below are still
                  saved to the API for production.
                </p>
              )}
              <label>
                RTMP push link — rtmPpushLink
                <input
                  value={form.rtmPpushLink}
                  onChange={(e) => setForm({ ...form, rtmPpushLink: e.target.value })}
                  placeholder="rtmp://host:1935"
                />
              </label>
              <label>
                WebRTC playback — streamLinkWebRTC
                <input
                  value={form.streamLinkWebRTC}
                  onChange={(e) =>
                    setForm({ ...form, streamLinkWebRTC: e.target.value })
                  }
                  placeholder="https://host/stream/device{id}/"
                />
              </label>
              <label>
                HLS playback — streamLinkHLS
                <input
                  value={form.streamLinkHLS}
                  onChange={(e) => setForm({ ...form, streamLinkHLS: e.target.value })}
                  placeholder="http://host:8888/device{id}/index.m3u8"
                />
              </label>
            </fieldset>
            <div className="modal-check-row">
              <label className="toggle-chip">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
                <span>isActive — start worker / ingest pipeline</span>
              </label>
              <label className="toggle-chip">
                <input
                  type="checkbox"
                  checked={form.isAiProcess}
                  onChange={(e) => setForm({ ...form, isAiProcess: e.target.checked })}
                />
                <span>isAiProcess — YOLO analytics + WebSocket payloads</span>
              </label>
            </div>
            <div className="actions-row">
              <button type="button" className="primary-btn" onClick={saveDevice}>
                Save
              </button>
              <button type="button" onClick={closeModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
